/**
 * 2026-10-08 (Manak, approved) — web triage desk "WhatsApp ↗": send the lead
 * to the assigned dealer from the coordinator's own WhatsApp, then mark it
 * sent. Same box as the Triage phone app:
 *   • SEND TO — the dealer's saved numbers, or "Another number…" (10-digit
 *     Indian mobile check);
 *   • the message from GET /leads/:id/dealer-message (ONE builder shared with
 *     the phone), editable;
 *   • "Open WhatsApp ↗" opens wa.me in a new tab — the page never sends;
 *   • then "Did you send it?" → "✓ Mark as sent" runs the desk's EXISTING
 *     mark-as-sent action (POST /leads/:id/dealer-notified).
 */
import { useEffect, useId, useState } from 'react';

type Contact = { label: string; phone: string };
type Loaded = { text: string; contacts: Contact[]; dealerName: string };

/** Typed number → 10 digits, or null unless it is an Indian mobile. */
export function normalizeDealerNumber(raw: string): string | null {
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  if (d.length !== 10 || !/^[6-9]/.test(d)) return null;
  return d;
}

export function dealerWhatsAppUrl(phone10: string, text: string): string {
  return `https://wa.me/91${phone10}?text=${encodeURIComponent(text)}`;
}

const pretty = (d: string) => (d.length === 10 ? `${d.slice(0, 5)} ${d.slice(5)}` : d);

export function DealerWhatsAppDialog({
  leadId,
  dealerName,
  sent,
  onMarkSent,
  onClose,
}: {
  leadId: string;
  dealerName: string | null;
  /** Already marked sent → "Send again": no "Did you send it?" afterwards. */
  sent: boolean;
  onMarkSent: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const [data, setData] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sel, setSel] = useState<number>(0); // -1 = Another number…
  const [text, setText] = useState('');
  const [other, setOther] = useState('');
  const [otherError, setOtherError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`/api/v1/leads/${leadId}/dealer-message`, { credentials: 'include' });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
        const d = json?.data ?? {};
        const contacts: Contact[] = Array.isArray(d.contacts)
          ? d.contacts
              .map((c: any) => ({ label: String(c?.label ?? '').trim(), phone: normalizeDealerNumber(String(c?.phone ?? '')) ?? '' }))
              .filter((c: Contact) => c.label && c.phone)
          : [];
        if (!alive) return;
        setData({ text: String(d.text ?? ''), contacts, dealerName: String(d.dealerName ?? dealerName ?? '').trim() });
        setText(String(d.text ?? ''));
        setSel(contacts.length ? 0 : -1);
      } catch (e) {
        if (alive) setLoadError((e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, [leadId, dealerName]);

  const name = data?.dealerName || dealerName || 'dealer';
  const title = `Send lead to ${name}`;

  function openWhatsApp() {
    if (!data) return;
    let phone: string;
    if (sel >= 0) {
      phone = data.contacts[sel].phone;
    } else {
      const n = normalizeDealerNumber(other);
      if (!n) {
        setOtherError('Enter a 10-digit mobile number');
        return;
      }
      phone = n;
    }
    window.open(dealerWhatsAppUrl(phone, text), '_blank', 'noopener,noreferrer');
    if (sent) {
      onClose();
      return;
    }
    setAsking(true);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">{title}</h2>

        {loadError ? (
          <>
            <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{loadError}</div>
            <button type="button" onClick={onClose} className="mt-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Close</button>
          </>
        ) : !data ? (
          <div className="mt-3 text-sm text-gray-500">Loading…</div>
        ) : asking ? (
          <div className="mt-3">
            <div className="text-base font-medium text-gray-900">Did you send it?</div>
            <p className="mt-1 text-sm text-gray-600">Mark this lead as sent to {name}.</p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => { onMarkSent(); onClose(); }}
                className="flex-1 rounded border border-green-600 bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700"
              >
                ✓ Mark as sent
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Not sent
              </button>
            </div>
          </div>
        ) : (
          <>
            <fieldset className="mt-3">
              <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">Send to</legend>
              <div className="mt-1.5 flex flex-col gap-1.5">
                {data.contacts.map((c, i) => (
                  <label key={`${c.phone}-${i}`} className={`flex items-center gap-2 rounded border px-3 py-2 text-sm cursor-pointer ${sel === i ? 'border-green-700 bg-green-50' : 'border-gray-200'}`}>
                    <input type="radio" name="dealer-send-to" checked={sel === i} onChange={() => { setSel(i); setOtherError(null); }} aria-label={c.label} />
                    <span className="flex-1 font-medium text-gray-900">{c.label}</span>
                    <span className="text-xs text-gray-500">{pretty(c.phone)}</span>
                  </label>
                ))}
                <label className={`flex items-center gap-2 rounded border px-3 py-2 text-sm cursor-pointer ${sel === -1 ? 'border-green-700 bg-green-50' : 'border-gray-200'}`}>
                  <input type="radio" name="dealer-send-to" checked={sel === -1} onChange={() => setSel(-1)} aria-label="Another number…" />
                  <span className="flex-1 font-medium text-gray-900">Another number…</span>
                </label>
                {sel === -1 && (
                  <div>
                    <input
                      type="tel"
                      aria-label="Mobile number"
                      placeholder="10-digit mobile number"
                      value={other}
                      onChange={(e) => { setOther(e.target.value); setOtherError(null); }}
                      className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-green-600"
                    />
                    {otherError && <div className="mt-1 text-xs text-red-600">{otherError}</div>}
                  </div>
                )}
              </div>
            </fieldset>

            <label className="mt-3 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Message (edit if needed)</span>
              <textarea
                aria-label="Message (edit if needed)"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={9}
                className="mt-1.5 w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-green-600"
              />
            </label>

            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={openWhatsApp}
                className="flex-1 rounded bg-[#25D366] px-3 py-2 text-sm font-medium text-white hover:brightness-95"
              >
                Open WhatsApp ↗
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
